import { inject, Injectable } from "@angular/core";
import { UserStoreService } from "../../../services/store/user/user.store.service";
import { SignalSubscriptionImpl } from "../data-subscription";
import { CalculationPastStoreService } from "../../../services/store/calculation-past/calculation-past.store.service";
import { getName } from "@bitwarden/web-vault/app/shared/utils/helper-string";
import type { TableSource } from "./transaction-table";
import { TransactionStatusEnum } from "@bitwarden/web-vault/app/models/enum/transactionType";
import { BalanceAlignmentDescription } from "@bitwarden/web-vault/app/models/enum/balance.enum";
import {
  ALIGNMENT_BALANCE_RESET,
  GLOSS_VAULT,
} from "@bitwarden/web-vault/app/models/constants/global.constants";
import { GranularityFilter } from "@bitwarden/web-vault/app/gloss/views/views-control/views-customize/granularity.filter";
import { TransactionView } from "@bitwarden/web-vault/app/models/view/transaction/transaction.view";

// Manual-balance descriptions generated before #4075 replaced the default with
// ALIGNMENT_BALANCE_RESET - e.g. "Opening balance (opening) 500" or
// "Fake Transaction (opening) 500" (see the prior default in
// TransactionService.createAccountAlignmentTransaction). Treated as "no real user note" so
// pre-existing rows still collapse to the clean label instead of showing the raw legacy text.
const LEGACY_BALANCE_ALIGNMENT_DESCRIPTION =
  /^(.+\sbalance\s\(.+\)|Fake Transaction\s\(.+\))\s-?[\d,]+(\.\d+)?$/i;

// Exported so this edge case (legacy generated descriptions predating #4075) can be unit
// tested without instantiating the service - see past-transaction-table.subscription.spec.ts.
export function isLegacyBalanceAlignmentDescription(
  description: string | null | undefined,
): boolean {
  return !!description && LEGACY_BALANCE_ALIGNMENT_DESCRIPTION.test(description);
}

// Exported so callers outside this table (e.g. search-bar.component.ts's #5058 direct-open path)
// can apply the exact same "is this a system-generated, zero-amount balance row the table itself
// never shows" check, instead of re-deriving it and risking drift.
export function isHiddenSystemBalanceTransaction(transaction: TransactionView): boolean {
  return (
    (transaction.definition === TransactionStatusEnum.closing ||
      transaction.definition === TransactionStatusEnum.opening ||
      transaction.definition === TransactionStatusEnum.manual_balance ||
      transaction.definition === TransactionStatusEnum.empty_account_balance) &&
    // c_valuation is only set once normalization resolves (setValuationOnTransaction), so an
    // unresolved valuation must not throw here - treat it as "not a hidden zero-amount row".
    transaction.c_valuation?.normalizedValue?.amount === 0
  );
}

// Exported for the same reason - renames the legacy "Gloss Vault" institution label to the current
// one (GLOSS Credits), so the table and the search-opened detail panel can't drift apart.
export function resolveInstitutionName(institutionName: string): string {
  return institutionName === "Gloss Vault" ? GLOSS_VAULT : institutionName;
}

// Exported for the same reason - the display description the table applies to a transaction,
// collapsing bank-imported balance-reset rows (including legacy pre-#4075 generated text) down to
// a single clean label instead of the raw internal description.
export function resolveTransactionDescription(transaction: TransactionView): string {
  return transaction.bankImportedBalance != null &&
    (!transaction.description ||
      transaction.description === BalanceAlignmentDescription.Generated ||
      transaction.description === BalanceAlignmentDescription.Starting ||
      isLegacyBalanceAlignmentDescription(transaction.description))
    ? ALIGNMENT_BALANCE_RESET
    : transaction.description;
}

@Injectable({
  providedIn: "root",
})
export class PastTransactionTableSubscription extends SignalSubscriptionImpl<TableSource[]> {
  userStoreService = inject(UserStoreService);
  calculationStoreService = inject(CalculationPastStoreService);
  protected granularityFilter = inject(GranularityFilter);

  get source() {
    const accountViews = this.calculationStoreService.filteredAccounts.collection();
    const institutionViews = this.userStoreService.institutions.institutionViews();
    const transactionViews = this.calculationStoreService.filteredTransactions.collection();
    const balance = this.calculationStoreService.balanceByTransaction.balances();
    const granularity = this.calculationStoreService.groupings.selected().granularity;

    if (!transactionViews) {
      return [];
    }

    return transactionViews
      .filter((t) => !isHiddenSystemBalanceTransaction(t))
      .map((transaction) => ({
        account: getName(accountViews, transaction.accountLink.id),
        //here we will update the existing instititon name to the new one(GLOSS Credits)
        institution: resolveInstitutionName(
          getName(institutionViews, transaction.c_institutionLink.id),
        ),
        origin: transaction.origin,
        symbol: transaction.c_valuation.normalizedValue.symbol,
        amount: transaction.c_valuation.normalizedValue.amount,
        transactionView: transaction,
        transactionDate: transaction.postDate.toISOString(),
        description: resolveTransactionDescription(transaction),
        balance: balance?.get(transaction.id) || 0,
        accountView: accountViews.find((acc) => acc.id === transaction.accountLink.id),
        currency: accountViews.find((acc) => acc.id === transaction.accountLink.id)?.currency,
        granularity: granularity,
        status: transaction.status,
      }));
  }
}
